/**
 * Collapsing a shoot's bursts into stacks, and expanding them again.
 * Pure module: no imports, no I/O.
 *
 * Forty frames of one heron taking off are one decision, not forty. Collapsed,
 * the strip shows the frame worth looking at and says how many are behind it;
 * expanded, it shows them all. The photographer moves between decisions rather
 * than between frames, which is the whole point.
 */

export interface Marked {
  burstId: number | null;
  isPick: boolean;
}

export interface Stack<T> {
  /** The frame shown when the stack is collapsed. */
  face: T;
  /** Every frame in the burst, in capture order. Length 1 for an ungrouped frame. */
  frames: T[];
  burstId: number | null;
}

/**
 * Groups an ordered list into stacks by burst.
 *
 * Only *consecutive* frames of the same burst group together. A burst id that
 * reappears after an interruption starts a new stack, because a stack that
 * silently gathered frames from either side of something else would misrepresent
 * the order the photographer is walking through.
 */
export function stacksOf<T extends { id: number }>(
  items: T[],
  markers: Map<number, Marked>,
): Stack<T>[] {
  const stacks: Stack<T>[] = [];

  for (const item of items) {
    const burstId = markers.get(item.id)?.burstId ?? null;
    const open = stacks[stacks.length - 1];

    if (burstId !== null && open && open.burstId === burstId) {
      open.frames.push(item);
      // The pick is what the stack should show. Falling back to the first frame
      // means a stack always has a face, even before the model has run.
      if (markers.get(item.id)?.isPick && !markers.get(open.face.id)?.isPick) open.face = item;
    } else {
      stacks.push({ face: item, frames: [item], burstId });
    }
  }

  return stacks;
}

/**
 * The frames actually shown, given which stacks are open.
 *
 * A stack of one is never a stack: there is nothing to collapse and a count
 * badge reading "1" would be noise.
 */
export function flatten<T extends { id: number }>(
  stacks: Stack<T>[],
  expanded: ReadonlySet<number>,
): T[] {
  return stacks.flatMap((stack) =>
    stack.frames.length === 1 || (stack.burstId !== null && expanded.has(stack.burstId))
      ? stack.frames
      : [stack.face],
  );
}

/** How many frames sit behind each visible one. 1 means the frame stands alone. */
export function depthOf<T extends { id: number }>(
  stacks: Stack<T>[],
  expanded: ReadonlySet<number>,
): Map<number, number> {
  const depth = new Map<number, number>();
  for (const stack of stacks) {
    const collapsed =
      stack.frames.length > 1 && (stack.burstId === null || !expanded.has(stack.burstId));
    if (collapsed) depth.set(stack.face.id, stack.frames.length);
  }
  return depth;
}
